import { useState } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import { Copy, ExternalLink, Link2Off, Pencil } from 'lucide-react';
import { safeHref } from '@kaba/shared';
import { toast } from '../../lib/toast';
import { Button, cx, Sheet } from '../ui/ui';

/*
 * Prüfbericht 0.63, U01: Links im Editor öffnen, bearbeiten, kopieren und entfernen.
 * - Steht der Cursor in einem Link (oder wird er angetippt), erscheint eine kleine Leiste mit der Zieladresse.
 * - „Öffnen“ ist ein echter Link (target=_blank, rel=noopener) – die Nutzergeste öffnet ihn direkt, ohne dass
 *   ein Speichern dazwischenkommt; Entwurf, Fokus und Cursor bleiben im Editor.
 * - Bearbeiten in einem Dialog mit beschrifteten Feldern statt window.prompt; nur http(s) und mailto.
 */

/** Adresse prüfen und ergänzen („example.com“ → „https://example.com“); null = nicht zulässig */
export function normalizeHref(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return safeHref(v);
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return safeHref(`mailto:${v}`);
  if (/^[^\s/]+\.[^\s]+/.test(v)) return safeHref(`https://${v}`);
  return null;
}

const shown = (href: string) => {
  try {
    const u = new URL(href);
    if (u.protocol === 'mailto:') return u.pathname;
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}${u.search}`;
  } catch {
    return href;
  }
};

/** Leiste unter der Werkzeugleiste, solange der Cursor in einem Link steht */
export function LinkBar({ editor, onEdit }: { editor: Editor; onEdit: () => void }) {
  const href = useEditorState({ editor, selector: ({ editor: e }) => (e && e.isActive('link') ? ((e.getAttributes('link').href as string | undefined) ?? null) : null) });
  if (!href) return null;
  const safe = safeHref(href);
  const btn = 'inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-sm hover:bg-black/5 sm:min-h-8 dark:hover:bg-white/10';
  return (
    <div role="group" aria-label="Link" className="flex flex-wrap items-center gap-1 border-b border-[var(--line)] bg-black/[0.02] px-2 py-1 dark:bg-white/[0.03]">
      <span className="min-w-0 basis-full truncate px-1 text-sm text-[var(--ink-soft)]" title={href}>
        {shown(href)}
      </span>
      {safe && (
        <a href={safe} target="_blank" rel="noopener noreferrer nofollow" className={cx(btn, 'text-accent-fg')} onMouseDown={(e) => e.preventDefault()}>
          <ExternalLink size={15} aria-hidden /> Öffnen
        </a>
      )}
      <button type="button" className={btn} onMouseDown={(e) => e.preventDefault()} onClick={onEdit}>
        <Pencil size={15} aria-hidden /> Bearbeiten
      </button>
      <button
        type="button"
        className={btn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(href);
            toast.info('Link kopiert.');
          } catch {
            toast.error('Kopieren nicht möglich – bitte die Adresse markieren und kopieren.');
          }
        }}
      >
        <Copy size={15} aria-hidden /> Kopieren
      </button>
      <button
        type="button"
        className={btn}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
      >
        <Link2Off size={15} aria-hidden /> Link entfernen
      </button>
    </div>
  );
}

/** Werkzeugleiste: Link anlegen/bearbeiten; liefert Dialog und Öffner */
export function useLinkDialog(editor: Editor | null) {
  const [open, setOpen] = useState<{ text: string; href: string; from: number; to: number; existing: boolean } | null>(null);
  const start = () => {
    if (!editor) return;
    const existing = editor.isActive('link');
    if (existing) editor.chain().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    setOpen({ text: editor.state.doc.textBetween(from, to, ' '), href: existing ? ((editor.getAttributes('link').href as string) ?? '') : '', from, to, existing });
  };
  const dialog = open && editor ? (
    <LinkDialog
      initial={open}
      onClose={() => {
        setOpen(null);
        editor.chain().focus().setTextSelection({ from: open.from, to: open.to }).run();
      }}
      onApply={(text, href) => {
        const chain = editor.chain().focus().setTextSelection({ from: open.from, to: open.to });
        const label = text.trim() || shown(href);
        if (open.from === open.to || label !== open.text) chain.insertContent({ type: 'text', text: label, marks: [{ type: 'link', attrs: { href } }] }).run();
        else chain.setLink({ href }).run();
        setOpen(null);
      }}
      onRemove={
        open.existing
          ? () => {
              editor.chain().focus().setTextSelection({ from: open.from, to: open.to }).unsetLink().run();
              setOpen(null);
            }
          : undefined
      }
    />
  ) : null;
  return { start, dialog };
}

function LinkDialog({
  initial,
  onClose,
  onApply,
  onRemove,
}: {
  initial: { text: string; href: string; existing: boolean };
  onClose: () => void;
  onApply: (text: string, href: string) => void;
  onRemove?: () => void;
}) {
  const [text, setText] = useState(initial.text);
  const [href, setHref] = useState(initial.href || '');
  const [error, setError] = useState<string | null>(null);
  const field = 'min-h-11 rounded-lg border border-[var(--line)] bg-transparent px-3 text-base sm:text-sm';
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={initial.existing ? 'Link bearbeiten' : 'Link einfügen'} side="center">
      <form
        className="flex flex-col gap-3 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          const ok = normalizeHref(href);
          if (!ok) {
            setError(href.trim() ? 'Diese Adresse geht nicht. Erlaubt sind Internetadressen (https://…) und E-Mail-Adressen.' : 'Bitte eine Adresse eingeben.');
            return;
          }
          onApply(text, ok);
        }}
      >
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Linktext</span>
          <input className={field} value={text} onChange={(e) => setText(e.target.value)} placeholder="Wird sonst aus der Adresse gebildet" />
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Zieladresse</span>
          <input
            className={field}
            autoFocus
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={href}
            aria-invalid={!!error}
            aria-describedby={error ? 'link-error' : undefined}
            onChange={(e) => (setHref(e.target.value), setError(null))}
            placeholder="https://"
          />
        </label>
        {error && (
          <p id="link-error" role="alert" className="text-sm text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {onRemove && (
            <Button type="button" variant="ghost" className="mr-auto" onClick={onRemove}>
              <Link2Off size={15} aria-hidden /> Link entfernen
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary">
            Anwenden
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
